import {
  CAlert,
  CBadge,
  CButton,
  CCol,
  CForm,
  CFormGroup,
  CInput,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CSwitch,
  CTextarea,
} from "@coreui/react";
import React, { useEffect, useState } from "react";
import {
  useCreateModel,
  useGetAllModelTags,
  useSyncRemoteModel,
  useUpdateRemoteModelUrl,
} from "../models/ModelService";

import CIcon from "@coreui/icons-react";
import { ListSelect } from "src/reusable";
import { Loading } from "src/reusable/LoadingSpinner";
import LoadingOverlay from "react-loading-overlay-ts";
import { ModelDetailsButton } from "../models/ModelButtons";
import PropTypes from "prop-types";
import TagSelect from "src/reusable/components/select/tag-select";
import { determineLoginPlatformType } from "src/reusable/Functions";
import { useProducts } from "../products/ProductServices";

export const ProductSelect = (props) => {
  const { data: productList } = useProducts();
  let displayItems = [];
  if (productList) {
    displayItems = productList.map((product) => {
      return {
        id: product.id,
        name: product.description,
      };
    });
  }
  return (
    <ListSelect
      handleSelectedValue={(x) => {
        props.handleSelectedProducts(x);
      }}
      items={displayItems}
      selectDomID={"product-selector"}
      disabled={!displayItems || props.disabled}
      multiple={props.allowMultiple}
      selectedValue={props.selectedProducts}
    />
  );
};

ProductSelect.propTypes = {
  disabled: PropTypes.bool,
  handleSelectedProducts: PropTypes.func.isRequired,
  selectedProducts: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  allowMultiple: PropTypes.bool,
};

ProductSelect.defaultProps = {
  disabled: false,
  allowMultiple: true,
};

const validateModel = (model) => {
  let errors = [];
  if (model.model.name.length === 0) {
    // validate name
    errors.push("Name cannot be empty.");
  }
  if (!model.product_fks || model.product_fks.length === 0) {
    // validate parent fks
    errors.push("Model needs to be associated with at least one product.");
  }
  if (
    model.remoteModel &&
    (!model.remoteModelDetails?.url || !model.remoteModelDetails?.credentials)
  ) {
    // validate that if a remoteModel is selected, the url and credentials are provided
    errors.push(
      "If remote model is selected, please provide a url and credentials.",
    );
  }
  return errors;
};

const ModelEditor = (props) => {
  return (
    <CForm>
      <CFormGroup row>
        <CLabel className="col-sm-2 col-form-label">Name</CLabel>
        <CCol sm={10}>
          {props.mode === "create" ? (
            <CInput
              id="edit-model-name-input"
              type="text"
              name="name"
              placeholder="Name"
              value={props.name}
              onChange={(e) => props.setName(e.target.value)}
            />
          ) : (
            <CLabel
              id="edit-model-name-label"
              className="col-sm-9 col-form-label"
            >
              {props.name}
            </CLabel>
          )}
        </CCol>
      </CFormGroup>
      <CFormGroup row>
        <CLabel className="col-sm-2 col-form-label">Description</CLabel>
        {props.mode === "view" ? (
          <CLabel
            id="edit-model-description-label"
            className="col-sm-9 col-form-label"
          >
            {props.description}
          </CLabel>
        ) : (
          <CCol sm={10}>
            <CTextarea
              id="edit-model-description-textarea"
              type="text"
              rows="2"
              name="description"
              placeholder="Description"
              value={props.description}
              onChange={(e) => props.setDescription(e.target.value)}
            />
          </CCol>
        )}
      </CFormGroup>
      <CFormGroup row>
        <CLabel className="col-sm-2 col-form-label">Tags</CLabel>
        <CCol sm={10}>
          <TagSelect
            value={props.selectedTags}
            onChange={(newTags) => {
              props.setSelectedTags(newTags);
            }}
            availableTags={props.availableTags}
            setAvailableTags={props.setAvailableTags}
            placeholder="Dataset Tags"
            allowCreation={true}
            isSmText={true}
          />
        </CCol>
      </CFormGroup>
      <CFormGroup row>
        <CLabel className="col-sm-2 col-form-label">Product(s)</CLabel>
        <CCol sm={10}>
          <ProductSelect
            id="edit-model-products-select"
            handleSelectedProducts={props.setProductFks}
            selectedProducts={props.productFks}
            disabled={props.mode === "view"}
          />
        </CCol>
      </CFormGroup>
      <CFormGroup row>
        <CLabel className="col-sm-2 col-form-label">
          Remote Model{" "}
          <CBadge
            color="secondary"
            style={{
              fontSize: "75%",
            }}
          >
            <i>Preview</i>
          </CBadge>
        </CLabel>
        <CCol sm={10}>
          <CSwitch
            id="edit-model-remote-model"
            name="edit-model-remote-model"
            role="switch"
            aria-checked={props.remoteModel}
            aria-label="Remote Model Preview"
            value={props.remoteModel}
            color={props.remoteModel ? "success" : ""}
            onChange={(e) => {
              props.setRemoteModel(e.target.checked);
            }}
            disabled={props.mode !== "create"}
          />
        </CCol>
      </CFormGroup>
      {props.remoteModel && (
        <div style={{ fontSize: "80%" }}>
          <CFormGroup row>
            <CCol sm="1" />
            <CLabel className="col-sm-3 col-form-label">URL</CLabel>
            <CCol sm={8}>
              {props.mode === "create" ? (
                <CInput
                  id="edit-remote-model-url"
                  type="text"
                  name="remote-model-url"
                  placeholder="url"
                  value={props.remoteModelDetails?.url}
                  onChange={(e) =>
                    props.setRemoteModelDetails({
                      ...props.remoteModelDetails,
                      url: e.target.value,
                    })
                  }
                />
              ) : (
                <CLabel
                  id="edit-remote-model-url"
                  className="col-sm-8 col-form-label"
                >
                  {props.remoteModelDetails?.url}
                </CLabel>
              )}
            </CCol>
          </CFormGroup>
          <CFormGroup row>
            <CCol sm="1" />
            <CLabel className="col-sm-3 col-form-label">Credentials</CLabel>
            <CCol sm={8}>
              {(props.mode === "create" || props.mode === "edit") && (
                <CInput
                  id="edit-remote-model-credentials"
                  type="password"
                  name="remote-model-credentials"
                  placeholder="credentials"
                  value={props.remoteModelDetails?.credentials}
                  onChange={(e) =>
                    props.setRemoteModelDetails({
                      ...props.remoteModelDetails,
                      credentials: e.target.value,
                    })
                  }
                />
              )}
            </CCol>
          </CFormGroup>
        </div>
      )}
    </CForm>
  );
};

ModelEditor.propTypes = {
  mode: PropTypes.oneOf(["create", "edit", "view"]).isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  productFks: PropTypes.string.isRequired,
  remoteModel: PropTypes.bool.isRequired,
  remoteModelDetails: PropTypes.object,
  setName: PropTypes.func.isRequired,
  setDescription: PropTypes.func.isRequired,
  setSelectedTags: PropTypes.func,
  selectedTags: PropTypes.array,
  setAvailableTags: PropTypes.func,
  availableTags: PropTypes.array,
  setProductFks: PropTypes.func.isRequired,
  setRemoteModel: PropTypes.func.isRequired,
  setRemoteModelDetails: PropTypes.func.isRequired,
};

ModelEditor.defaultProps = {
  model: {
    model: {
      name: "",
      description: "",
    },
    product_fks: [],
    parent_fks: [],
  },
};

export const CreateModelModal = (props) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [productFks, setProductFks] = useState("");
  const [remoteModel, setRemoteModel] = useState(false);
  const [remoteModelDetails, setRemoteModelDetails] = useState({});
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: createModel,
    isLoading: isCreateModelLoading,
    status: createModelStatus,
    isError: isCreateModelError,
    error: createModelError,
    data: newModel,
    reset: resetCreateModel,
  } = useCreateModel();

  const { data: modelTags, isSuccess: isModelTagsSuccess } =
    useGetAllModelTags();

  useEffect(() => {
    if (isModelTagsSuccess && modelTags) {
      setAvailableTags(modelTags.map((tag) => tag.tag));
    }
  }, [isModelTagsSuccess, modelTags]);

  const resetAfterClose = () => {
    setName("");
    setDescription("");
    setProductFks("");
    setErrorMessage(null);
    resetCreateModel();
  };
  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };
  // If there was a error creating the dataset, get rid of it!
  if (isCreateModelError) {
    let newErrorMessage = String(createModelError);
    if (createModelError?.response?.data?.detail) {
      newErrorMessage =
        "Server Error " +
        createModelError.response.status +
        ": " +
        JSON.stringify(createModelError.response.data.detail);
    }
    if (newErrorMessage !== errorMessage) {
      setErrorMessage(newErrorMessage);
    }
  }

  const onSubmit = () => {
    let model = {
      model: {
        name: name,
        description: description,
        tags: selectedTags,
        remote_model: remoteModel,
      },
      product_fks: productFks,
    };
    if (remoteModel) {
      model.model.remote_model_details = remoteModelDetails;
    }
    let errors = validateModel(model);
    if (errors.length === 0) {
      setErrorMessage(null);
      createModel(model);
      if (props.setModel) {
        props.setModel(model.model.name);
      }
    } else {
      setErrorMessage(errors.map((error, index) => <p key={index}>{error}</p>));
    }
  };

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h4>Create a New Model</h4>
          <span style={{ marginTop: "10px" }}>
            {`Create and save a new ${determineLoginPlatformType()} model for use in future automations.`}
          </span>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="create-model-close-button"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <LoadingOverlay
        active={isCreateModelLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Creating model..."
      >
        {createModelStatus === "success" ? (
          <CModalBody>
            <span id="create-model-success-message">
              {newModel.name} has been created!
              <br />
              <br />
              <div id="create-model-details-button">
                <ModelDetailsButton model={newModel} />
              </div>
            </span>
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
              <ModelEditor
                mode="create"
                name={name}
                description={description}
                setSelectedTags={setSelectedTags}
                selectedTags={selectedTags}
                setAvailableTags={setAvailableTags}
                availableTags={availableTags}
                productFks={productFks}
                remoteModel={remoteModel}
                remoteModelDetails={remoteModelDetails}
                setName={setName}
                setDescription={setDescription}
                setProductFks={setProductFks}
                setRemoteModel={setRemoteModel}
                setRemoteModelDetails={setRemoteModelDetails}
              />
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" style={{ textAlign: "right" }}>
                <CButton
                  id="submit-create-model-button"
                  color="success"
                  onClick={() => onSubmit()}
                >
                  Create New Model
                </CButton>
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

CreateModelModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setModel: PropTypes.func,
};

export const SyncRemoteModelModal = (props) => {
  const {
    mutate: syncRemoteModel,
    isIdle: isSyncRemoteModelIdle,
    isLoading: isSyncRemoteModelLoading,
    isError: isSyncRemoteModelError,
    isSuccess: isSyncRemoteModelSuccess,
    error: syncRemoteModelError,
    reset: resetSyncRemoteModel,
  } = useSyncRemoteModel();
  useEffect(() => {
    // only run once!
    if (
      props.modelId &&
      props.isVisible &&
      isSyncRemoteModelIdle &&
      (!isSyncRemoteModelSuccess || !isSyncRemoteModelError)
    ) {
      syncRemoteModel(props.modelId);
    }
  }, [
    props.isVisible,
    props.modelId,
    isSyncRemoteModelIdle,
    isSyncRemoteModelSuccess,
  ]);

  const resetAfterClose = () => {
    resetSyncRemoteModel();
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  const loading = (
    <img
      src="/images/NuclearN1-B.gif"
      width="25px"
      height="25px"
      style={{ float: "left", marginLeft: "10px" }}
    />
  );
  const complete = (
    <span
      className="text-success"
      style={{
        fontSize: "135%",
        marginLeft: "10px",
      }}
    >
      <b>✓</b>
    </span>
  );

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h4>Syncing Remote Model...</h4>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="sync-remote-model-close-button"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <CModalBody>
        {isSyncRemoteModelLoading && <>{loading}</>}
        {isSyncRemoteModelError && (
          <CAlert color="danger">
            Error:{" "}
            {syncRemoteModelError?.response?.data?.detail
              ? JSON.stringify(syncRemoteModelError?.response?.data?.detail)
              : "Unknown server error"}
          </CAlert>
        )}
        {isSyncRemoteModelSuccess && <>{complete} Model successfully synced!</>}
      </CModalBody>
    </CModal>
  );
};

SyncRemoteModelModal.propTypes = {
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export const UpdateRemoteUrlModal = (props) => {
  const [url, setUrl] = useState("");
  const {
    mutate: updateRemoteUrl,
    isLoading: isUpdateRemoteUrlLoading,
    isError: isUpdateRemoteUrlError,
    isSuccess: isUpdateRemoteUrlSuccess,
    error: updateRemoteUrlError,
    reset: resetUpdateRemoteUrl,
  } = useUpdateRemoteModelUrl();

  // Call onSuccess callback when update completes successfully
  useEffect(() => {
    if (isUpdateRemoteUrlSuccess && props.onSuccess) {
      props.onSuccess();
    }
  }, [isUpdateRemoteUrlSuccess]);

  const resetAfterClose = () => {
    setUrl("");
    resetUpdateRemoteUrl();
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  const onSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (url.trim()) {
      updateRemoteUrl({ modelId: props.modelId, url: url.trim() });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSubmit();
    }
  };

  const loading = (
    <img
      src="/images/NuclearN1-B.gif"
      width="25px"
      height="25px"
      style={{ float: "left", marginLeft: "10px" }}
    />
  );

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h4>Update Remote Model URL</h4>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="update-remote-url-close-button"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <CModalBody>
        {isUpdateRemoteUrlLoading && (
          <>{loading} Updating URL and syncing model...</>
        )}
        {isUpdateRemoteUrlError && (
          <CAlert color="danger">
            Error:{" "}
            {updateRemoteUrlError?.response?.data?.detail
              ? JSON.stringify(updateRemoteUrlError?.response?.data?.detail)
              : "Unknown server error"}
          </CAlert>
        )}
        {isUpdateRemoteUrlSuccess && (
          <CAlert color="success">URL successfully updated!</CAlert>
        )}
        {!isUpdateRemoteUrlLoading && !isUpdateRemoteUrlSuccess && (
          <CForm onSubmit={onSubmit}>
            <CFormGroup>
              <CLabel htmlFor="remote-url-input">New Remote URL</CLabel>
              <CInput
                id="remote-url-input"
                type="text"
                placeholder="https://..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </CFormGroup>
          </CForm>
        )}
      </CModalBody>
      {!isUpdateRemoteUrlLoading && !isUpdateRemoteUrlSuccess && (
        <CModalFooter>
          <CButton
            id="update-remote-url-confirm-button"
            color="primary"
            onClick={onSubmit}
            disabled={!url.trim()}
          >
            Confirm
          </CButton>
          <CButton color="secondary" onClick={() => onClose()}>
            Cancel
          </CButton>
        </CModalFooter>
      )}
    </CModal>
  );
};

UpdateRemoteUrlModal.propTypes = {
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
